import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import Typography from '@material-ui/core/Typography';
import Badge from '@material-ui/core/Badge';
import Hidden from '@material-ui/core/Hidden';
import Tooltip from '@material-ui/core/Tooltip';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import PlatformTable from './Components/PlatformTable';
import MissionPlayerGrid from './Components/MissionPlayerGrid';
import MissionPlaybackToolbar from './Components/MissionPlaybackToolbar';
import { MissionTable } from 'stserver-frontend-comp';
import AisTable from './Components/AisTable';
import AnnotationTable from './Components/AnnotationTable';
import { MapControlsMode } from './Map/UavMap';
import { appContext, missionsContext, platformsContext, serverContext, themeContext, translateMissions, translatePlatforms, getFromServer } from './App';
import queryString from 'query-string';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { TabPanel } from './Components/TabPanel';

const useStyles = makeStyles(theme => ({
    toolboxHeader: {
        flexGrow: 1,
    },
    accordionDetails: {
        padding: theme.spacing(0.2),
    },
    root: {
        width: '100%',
        overflowX: 'hidden', // Prevent Grid negative margins from forcing a horizontal scrollbar
        overflowY: 'hidden', // Hide stray vertical scrollbar when content fits
        '& .MuiTextField-root': {
            margin: theme.spacing(1),
        },
        '& .MuiAccordion-root.Mui-expanded': {
            paddingBottom: 0,
            paddingTop: 0,
            marginBottom: 4
        },
    },
    tabPanel: {
        padding: 0, // Remove padding from TabPanel
    },
    tabRoot: {
        minHeight: 20, // Slimmer tabs container
        padding: 0,
    },
    tabLabel: {
        fontSize: '0.9rem', // Reduced font size
        padding: '0px 8px',
        minHeight: 28,
        lineHeight: 1.05,
    },
    tabIndicator: {
        height: 1, // Reduced indicator height
    },

}));



/**
 * Missions
 * 
 * @param  {} props
 */
export default function Missions(props) {
    const classes = useStyles();
    const { server, dispatchServer } = useContext(serverContext);
    const { dispatchMissions } = useContext(missionsContext);
    const { platforms } = useContext(platformsContext);
    const { dispatchApp } = useContext(appContext);
    const themeCtx = useContext(themeContext);
    const themeMode = themeCtx?.mode === 'dark' ? 'dark' : 'light';
    const [activeSensors, setActiveSensors] = useState([]);
    const [selectedMission, setSelectedMission] = useState(null);
    const [selectedAis, setSelectedAis] = useState(null);
    const [activeLiveSensors, setActiveLiveSensors] = useState([]);
    const [showSensorToolbar, setShowSensorToolbar] = useState(true);
    const [selectedControls, setSelectedControls] = useState([]);   // Map, KlvView etc
    const [selectMapControl, setSelectMapControl] = useState(undefined);
    const [queryParams, setQueryParams] = useState({});
    const [missionUpdateTime, setMissionUpdateTime] = useState(null);
    const [aoiControlsMode, setAoiControlsMode] = useState();
    const [takeSnapshot, setTakeSnapshot] = useState(false);
    const [tabValue, setTabValue] = useState(() => {
        const saved = localStorage.getItem('missionsTabValue');
        return saved !== null ? parseInt(saved, 10) : 0;
    });
    const [mapBoundingBox, setMapBoundingBox] = useState();
    const fallbackMapProps = {
        center: [41.35, 2.18],
        zoom: 11,
    };
    const toLatLngArray = (value) => {
        if (Array.isArray(value) && value.length >= 2) {
            const lat = Number(value[0]);
            const lon = Number(value[1]);
            if (Number.isFinite(lat) && Number.isFinite(lon)) {
                return [lat, lon];
            }
            return null;
        }
        if (value && typeof value === 'object') {
            const lat = Number(value.lat ?? value.latitude);
            const lon = Number(value.lng ?? value.lon ?? value.longitude);
            if (Number.isFinite(lat) && Number.isFinite(lon)) {
                return [lat, lon];
            }
        }
        return null;
    };
    const getSafeCenter = (value, fallback = fallbackMapProps.center) => {
        const normalized = toLatLngArray(value);
        return normalized ? normalized : [...fallback];
    };
    const getStoredMapProps = () => {
        try {
            const stored = localStorage.getItem("lastMapProps");
            if (!stored) {
                return { ...fallbackMapProps, center: [...fallbackMapProps.center] };
            }

            const saved = JSON.parse(stored);
            return {
                center: getSafeCenter(saved?.center),
                zoom: typeof saved?.zoom === 'number' && Number.isFinite(saved.zoom) ? saved.zoom : fallbackMapProps.zoom,
            };
        } catch (error) {
            console.warn('Failed to read last map props from localStorage:', error);
        }

        return { ...fallbackMapProps, center: [...fallbackMapProps.center] };
    };
    const initialMapProps = getStoredMapProps();
    const [mapCenter, setMapCenter] = useState(initialMapProps);
    const [annotationMapCenter, setAnnotationMapCenter] = useState([...initialMapProps.center]);
    const [selectedAnnotation, setSelectedAnnotation] = useState(null);
    const [annotationUpdate, setAnnotationUpdate] = useState(Date.now());
    const [selectedAnnotationId, setSelectedAnnotationId] = useState(); // this is used to store the map selected annotation id

    const timelineOpen = selectedControls && selectedControls.includes('timeline');


    useEffect(() => {

        try {

            if (props.mode === 'live')
                dispatchServer({ type: 'LIVE_STREAM_EVENTS', payload: 'SUBSCRIBE' });
        } catch (error) {
            console.log(error.message);
        }

        return () => {
            // Clean up playing sensors 
            setActiveSensors([]);
            setActiveLiveSensors([]);
            if (props.mode === 'live')
                dispatchServer({ type: 'LIVE_STREAM_EVENTS', payload: 'UNSUBSCRIBE' });
        };
    }, [props.mode, dispatchServer])

    useEffect(() => {

        let queryParams = {};
        if (queryString && props.search.length > 0)
            queryParams = queryString.parse(props.search);

        setQueryParams(queryParams);
    }, [props.search])

    useEffect(() => {
        if (props.mode !== 'live' || !Array.isArray(platforms.availablePlatforms) || platforms.availablePlatforms.length === 0) {
            return;
        }

        setActiveLiveSensors(prevActiveLiveSensors => {
            if (!Array.isArray(prevActiveLiveSensors) || prevActiveLiveSensors.length === 0) {
                return prevActiveLiveSensors;
            }

            const latestLiveSensors = translatePlatforms(platforms.availablePlatforms)
                .filter(row => row.parentId !== undefined)
                .reduce((sensorByKey, sensor) => {
                    sensorByKey[`${sensor.parentPlatformName}/${sensor.name}`] = sensor;
                    return sensorByKey;
                }, {});

            const refreshedLiveSensors = prevActiveLiveSensors
                .map(sensor => latestLiveSensors[`${sensor.parentPlatformName}/${sensor.name}`])
                .filter(Boolean);

            return refreshedLiveSensors.length > 0 ? refreshedLiveSensors : prevActiveLiveSensors;
        });
    }, [props.mode, platforms.availablePlatforms])

    const handleSensorsExpanded = expanded => {
        setShowSensorToolbar(expanded);
    }

    const handleSelectedSensors = sensors => {
        setActiveSensors(sensors);
    }

    const handleSelectedMission = mission => {
        setSelectedMission(mission);
        //   setSelectedMission(mission[0].mName);
        // FIXME:  Patch to properly update timeline
        if (props.mode !== 'live') {
            setTimeout(() => {
                setMissionUpdateTime(Date.now());
            }, 500);
        }
    }

    const handleSelectedAis = ais => {
        setSelectedAis(ais.mmsi)
    }

    const handleDeleteAis = async () => {
        try {
            await axios({
                url: `${server.serverHost}/api/aistargets`,
                method: 'DELETE',
                headers: { 'Authorization': `Basic ${server.token}` }
            });
            dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: `Ais targets deleted`, severity: 'info' } });
        } catch (error) {
            const message = error.response?.data?.message || error.message || 'Failed to delete Ais targets';
            dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message, severity: 'error' } });
        }
    }

    const handleDeleteVesselInfo = async () => {
        try {
            await axios({
                url: `${server.serverHost}/api/vesselinfo`,
                method: 'DELETE',
                headers: { 'Authorization': `Basic ${server.token}` }
            });
            dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: `Vessel Info deleted`, severity: 'info' } });
        } catch (error) {
            const message = error.response?.data?.message || error.message || 'Failed to delete Vessel Info';
            dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message, severity: 'error' } });
        }
    }

    const handleMapSelectedAis = (mmsi) => {
        setSelectedAis(mmsi)
    }

    const handleSelectedAnnotation = annotation => {
        setSelectedAnnotation(annotation);
        setSelectedAnnotationId(annotation._id);

        // const geoJson = toGeoJson(annotation);
        // const center = getGeoJsonCenter(geoJson);
        // setMapCenter({ ...mapCenter, center: [center[1], center[0]] })
    }

    const handleMapSelectedAnnotationId = (annotationId) => {
        setSelectedAnnotationId(annotationId);
    }

    const handleSelectedAnnotationUpdated = (annotation) => {
        setAnnotationUpdate(Date.now());
    }

    const mapBoundingBoxChange = (bbox, curMapCenter) => {
        setMapBoundingBox(bbox);
        const normalizedCenter = toLatLngArray(curMapCenter);
        if (normalizedCenter) {
            setAnnotationMapCenter(normalizedCenter);
            setMapCenter(prev => ({ ...prev, center: normalizedCenter }));
        }
    }

    const handleSelectedPlatform = (platform, sensors) => {

    }

    const handleSelectedLiveSensors = liveSensors => {
        setActiveLiveSensors(liveSensors);
    }

    const handleControlSelectionChange = controls => {
        setSelectedControls(controls);
    }

    // Called when Mission Controls Cfg is Loaded - klv, map, etc
    const handleMissionControlsCfgLoaded = controls => {
        setSelectedControls(controls);
    }

    const handleSelectedFilter = (selection, state, params) => {

        switch (selection) {

            case 'geo':
                setSelectMapControl(state ? MapControlsMode.FilterMissions : MapControlsMode.None);
                if (params && params.hasOwnProperty('aoiMode')) {
                    setAoiControlsMode(params.aoiMode);
                    if (state)
                        dispatchApp({ type: 'SHOW_SNACKBAR_MESSAGE', payload: { message: `Please select on a map the "Filter Missions" mode with the required (${params.aoiMode ? params.aoiMode.toUpperCase() : '-'}) AOI mode`, severity: 'info' } });
                }

                break;

            default:
                break;
        }
    }

    const handleSnapshot = () => {
        setTakeSnapshot(true);
    }

    const handleOnSnapshot = () => {
        setTakeSnapshot(false);
    }

    const handleApplyQueryFilter = async filter => {
        try {
            if (filter)
                sessionStorage.setItem('queryFilter', JSON.stringify(filter));
            else
                sessionStorage.removeItem("queryFilter");

            setAoiControlsMode(filter.aoiMode);

        } catch (error) {
        }

        setQueryParams(filter);

        //FIXME: These are not really needed. Have to remove them
        dispatchApp({ type: 'UPDATE_MISSIONS_FILTER', payload: filter });
        const resMissionList = await getFromServer(`missions/brief`, filter, server);
        dispatchMissions({ type: 'SET_MISSIONS', payload: translateMissions(resMissionList.data) });
    }

    const handleShareUrl = url => {
        try {
            window.open(`mailto:?subject=StServer%20video%20link&body=${url}`);
        } catch (error) {

        }
    }

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        localStorage.setItem('missionsTabValue', newValue.toString());
    };


    if (!server?.serverHost) {
        return <div>Loading server configuration...</div>;
    }

    return (
        <div className={classes.root} style={{ 'paddingLeft': '0px' }}>
            <Accordion defaultExpanded TransitionProps={{ unmountOnExit: false }} onChange={(event, expanded) => handleSensorsExpanded(expanded)}>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1a-content"
                    aria-label="Expand"
                    id="panel1a-header"
                >
                    <Grid container className={classes.toolboxHeader}
                        onClick={(event) => event.stopPropagation()}
                        onFocus={(event) => event.stopPropagation()}
                    >
                        <Hidden only={['xs', 'sm']}>
                            <Grid item xs={1} md={2} lg={2}>
                                <Tooltip title="Total sensors in the mission">
                                    <Badge badgeContent={activeSensors.length} color="primary" overlap="rectangular">
                                        <Typography noWrap variant="overline" display="block">Sensors</Typography>
                                    </Badge>
                                </Tooltip>
                            </Grid>
                        </Hidden>
                        {showSensorToolbar &&
                            <Grid item xs={10} md={10} lg={10} >
                                <MissionPlaybackToolbar mode={props.mode} activeSensors={activeSensors.length > 0 || activeLiveSensors.length > 0} onControlSelectionChange={handleControlSelectionChange} onMissionControlsCfgLoaded={handleMissionControlsCfgLoaded} snapshotEnabled={true} onSnapshot={handleSnapshot} />
                            </Grid>
                        }
                    </Grid>
                </AccordionSummary>
                <AccordionDetails classes={{ root: classes.accordionDetails }}>
                    <Grid container spacing={0}>
                        <Grid item xs={12} md={12} lg={12}>
                            <MissionPlayerGrid
                                mode={props.mode}
                                useLiveLowLatency={server.useLiveLowLatency}
                                useWebRtcVideo={server.useWebRtcVideo} webRtcService={server.webRtcService}
                                useLiveRtspHls={server.useLiveRtspHls}
                                mission={selectedMission}
                                sensors={props.mode === 'vod' ? activeSensors : activeLiveSensors}
                                controls={selectedControls} selectMapControl={selectMapControl}
                                onApplyQueryFilter={handleApplyQueryFilter}
                                timelineOpen={timelineOpen}
                                missionUpdateTime={missionUpdateTime}
                                mapCenter={mapCenter}
                                aoiControlsMode={aoiControlsMode}
                                takeSnapshot={takeSnapshot} onSnapshot={handleOnSnapshot} onSnapshotClose={() => setTakeSnapshot(false)}
                                selectedAis={selectedAis} onAisSelected={handleMapSelectedAis}
                                selectedAnnotation={selectedAnnotation}
                                annotationUpdate={annotationUpdate}
                                onMapBoundingBoxChange={mapBoundingBoxChange}
                                onAnnotationSelected={handleMapSelectedAnnotationId}
                            />
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>
            {
                <Grid container spacing={1}>
                    <Grid item xs={12} md={12} lg={12}>
                        <React.Fragment>
                            <Tabs value={tabValue} onChange={handleTabChange}
                                classes={{
                                    root: classes.tabRoot,
                                    indicator: classes.tabIndicator,
                                }}
                            >
                                <Tab label={props.mode === 'live' ? "Live" : "VOD"} classes={{ root: classes.tabLabel }} />
                                <Tab label={"Annotations"} classes={{ root: classes.tabLabel }} />
                                {server && server.useAis && <Tab label={"AIS"} classes={{ root: classes.tabLabel }} />}
                            </Tabs>
                            <TabPanel value={tabValue} index={0}>
                                {props.mode === 'live' ? (
                                    <PlatformTable
                                        title=' '
                                        onSelectPlatform={handleSelectedPlatform}
                                        onSelectedLiveSensors={handleSelectedLiveSensors}
                                    />
                                ) : (
                                    <MissionTable
                                        serverUrl={server.serverHost}
                                        serverToken={server.token}
                                        missionsFilter={queryParams}
                                        username={server.username}
                                        theme={themeMode}
                                        fitToRows={true}
                                        onMissionClick={handleSelectedMission}
                                        onSelectedSensors={handleSelectedSensors}
                                        onSelectedFilter={handleSelectedFilter}
                                        onApplyQueryFilter={handleApplyQueryFilter}
                                        onShare={handleShareUrl}
                                    />
                                )}
                            </TabPanel>
                            <TabPanel value={tabValue} index={1}>
                                <AnnotationTable mapBoundingBox={mapBoundingBox} selectedAnnotationId={selectedAnnotationId} lastMapCenter={annotationMapCenter} onAnnotationSelected={handleSelectedAnnotation} onAnnotationSelectionUpdated={handleSelectedAnnotationUpdated} />
                            </TabPanel>
                            {
                                server && server.useAis &&
                                <TabPanel value={tabValue} index={2}>
                                    <AisTable admin={server.userRole === 'superAdmin' || server.userRole === 'admin'} onAisTargetClick={handleSelectedAis} onDeleteAis={handleDeleteAis} onDeleteVesselInfo={handleDeleteVesselInfo} />
                                </TabPanel>
                            }
                        </React.Fragment>
                    </Grid>
                </Grid>
            }
        </div>

    );
}
